import { useState, useEffect } from 'react';
import { Link } from 'react-router';
import { Plus, BarChart3, History, MapPin, Users, LogOut } from 'lucide-react';
import { useAuth } from '../auth/AuthContext';
import { ProgramSwitcher } from '../components/ProgramSwitcher';
import { TeamStatsTiles } from '../components/TeamStatsTiles';
import { getGames } from '../utils/storage';
import { calculateTeamStats, getAllTeams, getAllCurlingClubs, getSheetsForClub } from '../utils/analytics';
import { Game } from '../types/curling';

export function Dashboard() {
  const { user, canWrite } = useAuth();

  const [games, setGames] = useState<Game[]>([]);
  const [selectedTeam, setSelectedTeam] = useState<string>('');
  const [selectedClub, setSelectedClub] = useState<string>('');

  useEffect(() => {
    let cancelled = false;

    getGames()
      .then(loadedGames => {
        if (cancelled) return;
        setGames(loadedGames);

        // Functional updates rather than reading selectedTeam/selectedClub, which
        // this effect does not depend on and would only ever see as their initial ''.
        const teams = getAllTeams(loadedGames);
        const clubs = getAllCurlingClubs(loadedGames);
        if (teams.length > 0) setSelectedTeam(current => current || teams[0]);
        if (clubs.length > 0) setSelectedClub(current => current || clubs[0]);
      })
      .catch(error => console.error('Failed to load games:', error));

    return () => {
      cancelled = true;
    };
  }, []);

  const clubs = getAllCurlingClubs(games);
  const teams = getAllTeams(games);

  const teamStats = selectedTeam ? calculateTeamStats(games, selectedTeam) : null;
  const sheetsForClub = selectedClub ? getSheetsForClub(games, selectedClub) : [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-950 via-blue-900 to-slate-900">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">Curling Codex Dashboard</h1>
            <p className="text-blue-300">Real-time game management, performance analytics and facility tracking</p>
          </div>
          <div className="flex gap-3 flex-wrap">
            <Link
              to="/options"
              className="flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/20 text-white px-4 py-2 rounded-lg transition-colors"
            >
              <LogOut className="w-5 h-5" />
              Exit
            </Link>
            <Link
              to="/CurlingCodex/history"
              className="flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/20 text-white px-4 py-2 rounded-lg transition-colors"
            >
              <History className="w-5 h-5" />
              Game History
            </Link>
            {/* Hidden for a viewer rather than shown and then refused. */}
            {canWrite && (
              <Link
                to="/CurlingCodex/game/new"
                className="flex items-center gap-2 bg-blue-500 hover:bg-blue-400 text-white px-4 py-2 rounded-lg transition-colors"
              >
                <Plus className="w-5 h-5" />
                New Game
              </Link>
            )}
          </div>
        </div>

        <ProgramSwitcher />

        {user && user.accounts.length === 0 ? (
          <div className="bg-white/8 backdrop-blur-sm border border-white/15 rounded-2xl shadow-xl shadow-black/20 p-12 text-center">
            <BarChart3 className="w-16 h-16 text-blue-300/70 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">No Program Yet</h3>
            <p className="text-blue-300">
              Start a program above to begin recording games, or ask someone to add you
              as a contributor or viewer on theirs.
            </p>
          </div>
        ) : games.length === 0 ? (
          <div className="bg-white/8 backdrop-blur-sm border border-white/15 rounded-2xl shadow-xl shadow-black/20 p-12 text-center">
            <BarChart3 className="w-16 h-16 text-blue-300/70 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">No Games Yet</h3>
            <p className="text-blue-300 mb-6">Start tracking your first curling game to see analytics</p>
            <Link
              to="/CurlingCodex/game/new"
              className="inline-flex items-center gap-2 bg-blue-500 hover:bg-blue-400 text-white px-6 py-3 rounded-lg transition-colors"
            >
              <Plus className="w-5 h-5" />
              Start First Game
            </Link>
          </div>
        ) : (
          <>
            {/* Team Stats Section */}
            {teamStats && teams.length > 0 && (
              <div className="bg-white/8 backdrop-blur-sm border border-white/15 rounded-2xl shadow-xl shadow-black/20 p-6 mb-8">
                <div className="mb-6">
                  <h2 className="text-xl font-semibold text-white mb-4">Team Statistics</h2>
                  <div className="flex flex-wrap gap-4 items-center justify-between">
                    <div className="flex gap-4 items-center">
                      <select
                        value={selectedTeam}
                        onChange={(e) => setSelectedTeam(e.target.value)}
                        className="bg-white/10 border border-white/20 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400/60"
                      >
                        {teams.map(team => (
                          <option key={team} value={team} className="bg-blue-950 text-white">{team}</option>
                        ))}
                      </select>
                    </div>
                    <Link
                      to="/CurlingCodex/history"
                      state={{ filterTeam: selectedTeam }}
                      className="flex items-center gap-2 bg-blue-500 hover:bg-blue-400 text-white px-4 py-2 rounded-lg transition-colors text-sm font-medium"
                    >
                      <Users className="w-4 h-4" />
                      View Games
                    </Link>
                  </div>
                </div>

                <TeamStatsTiles teamStats={teamStats} />
              </div>
            )}

            {/* Curling Club History */}
            {clubs.length > 0 && (
              <div className="bg-white/8 backdrop-blur-sm border border-white/15 rounded-2xl shadow-xl shadow-black/20 p-6 mb-8">
                <div className="mb-6">
                  <h2 className="text-xl font-semibold text-white mb-4">Curling Club History</h2>
                  <div className="flex gap-4 items-center">
                    <select
                      value={selectedClub}
                      onChange={(e) => setSelectedClub(e.target.value)}
                      className="bg-white/10 border border-white/20 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400/60"
                    >
                      {clubs.map(club => (
                        <option key={club} value={club} className="bg-blue-950 text-white">{club}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {sheetsForClub.length > 0 ? (
                  <div>
                    <h3 className="text-sm font-medium text-white mb-4 flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-blue-300" />
                      Sheets Used at {selectedClub}
                    </h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                      {sheetsForClub.map(sheet => {
                        const gamesOnSheet = games.filter(
                          g => g.curlingClub === selectedClub && g.sheetNumber === sheet
                        ).length;

                        return (
                          <Link
                            key={sheet}
                            to={`/CurlingCodex/club-sheet/${encodeURIComponent(selectedClub)}/${sheet}`}
                            className="p-4 bg-blue-500/15 border border-white/15 rounded-xl text-center hover:bg-blue-500/25 transition-colors cursor-pointer"
                          >
                            <div className="text-2xl font-bold text-white mb-1">
                              Sheet {sheet}
                            </div>
                            <div className="text-xs text-blue-300 mb-2">
                              {gamesOnSheet} {gamesOnSheet === 1 ? 'game' : 'games'}
                            </div>
                            <div className="text-xs text-blue-200 font-medium">
                              View Games →
                            </div>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <MapPin className="w-12 h-12 text-blue-300/70 mx-auto mb-2" />
                    <p className="text-blue-300">No sheets recorded for {selectedClub}</p>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}